import { useCallback, useMemo, useState } from 'react';
import { useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useIsLoggedIn, useIsLoggingIn } from '@order/AuthMachine';
import { useCustomerCredit } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useIsCustomerSessionReady } from '@order/hooks';
import { useCart } from '@order/hooks';

import type { CheckoutPayload } from '../../Checkout.utils';
import { useCheckoutTelemetry } from '../useCheckoutTelemetry';
import { useCreditCardOptions } from '../useCreditCardOptions';
import { useDeliveryTip } from '../useDeliveryTip';
import { useOrderAddress } from '../useOrderAddress';
import { useOrderValidation } from '../useOrderValidation';
import { useSubmitOrder } from '../useSubmitOrder';
import { useSyncDeliveryOrderDetails } from '../useSyncDeliveryOrderDetails';
import { useUpdateAddress } from '../useUpdateAddress';
import { useCheckoutFieldsValidation } from '../useValidateCheckoutFields';

export const useCheckoutData = ({
  replaceWithOrderStatus,
  handleInvalidWantedTime,
}: CheckoutDataProps) => {
  // ───── Checkout Payload ─────────────────────────────────────────
  const [payload, setPayload] = useState(defaultCheckoutPayload);

  // ───── Customer Data ────────────────────────────────────────────
  const customerData = useCustomer();
  const { customer, setCustomerData } = customerData;
  const creditCardOptions = useCreditCardOptions(customer);
  const isCustomerSessionReady = useIsCustomerSessionReady();

  // ───── Auth Status ──────────────────────────────────────────────
  const isLoggingIn = useIsLoggingIn();
  const isLoggedOut = !useIsLoggedIn();

  // ───── Cart Data ────────────────────────────────────────────────
  const { cart, isCartEmpty, isFetchingCart, cartOrderType, total } = useCart();
  const { id: orderId } = cart ?? {};
  const isDeliveryCart = cart?.orderType?.toLowerCase() === 'delivery';

  // ───── Delivery Tip ─────────────────────────────────────────────
  const deliveryTip = useDeliveryTip(payload);

  // ───── Credit Data ──────────────────────────────────────────────
  // This needs to be network-only due to a Gravy sync issue.
  // https://sweetgreen.atlassian.net/wiki/spaces/~331306269/pages/4472668234/Issue+Credit+Not+Available+Visible
  const { availableCredit, checkoutCredit, isLoadingCredits, refetchCredits } =
    useCustomerCredit({ deliveryTip: isDeliveryCart ? deliveryTip : 0 });

  // ───── Order Address ────────────────────────────────────────────
  const { orderAddress, hasAddress, addressId, isAddressReady } =
    useOrderAddress({
      cart,
      customer,
    });
  const { submittingAddress, handleUpdateAddress } = useUpdateAddress({
    addressId,
    customer,
    setCustomerData,
  });

  // ───── Telemetry ────────────────────────────────────────────────
  const { checkoutViewEvent } = useCheckoutTelemetry({
    customer,
    cart,
    cartOrderType,
    total,
  });

  // ───── Payload Validation ───────────────────────────────────────
  const {
    canSubmitOrder,
    cannotSubmitOrderReason,
    shouldBlockCheckoutOnPress,
    shouldShowErrorNotice,
  } = useOrderValidation({
    ...payload,
    cart,
  });

  // ───── CheckoutFields (dropoffLocation and CC) Validation ───────────────────────────────────────
  const {
    validateCheckoutFields,
    resetCheckoutFieldValidation,
    checkoutFieldsValidation,
  } = useCheckoutFieldsValidation({ orderType: cartOrderType });

  // ───── Cart Validation ──────────────────────────────────────────
  const hasCart = !isCartEmpty && Boolean(cart?.id);
  const hasAddressIfNeeded =
    !isDeliveryCart || Boolean(cart?.deliveryOrderDetail?.address?.id);

  // ───── Render Control ───────────────────────────────────────────
  const isLoadingCheckout = isFetchingCart || isLoggingIn || isLoadingCredits;
  const isCustomerValid = Boolean(isCustomerSessionReady && isAddressReady);
  const isCartValid = Boolean(isCustomerValid && hasCart && hasAddressIfNeeded);

  // ───── Order Submission ─────────────────────────────────────────
  const { submitting, validateRewardException, submitOrder } = useSubmitOrder();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const submitOrderCallback = useCallback(async () => {
    if (shouldShowErrorNotice) {
      validateCheckoutFields(payload);
    }

    if (shouldBlockCheckoutOnPress || !orderId) {
      addNoticeBanner({ text: cannotSubmitOrderReason, palette: 'caution' });

      return;
    }

    await submitOrder({
      input: { ...payload, orderId },
      handleInvalidWantedTime,
      replaceWithOrderStatus,
    });
  }, [
    shouldBlockCheckoutOnPress,
    shouldShowErrorNotice,
    cannotSubmitOrderReason,
    orderId,
    payload,
    validateCheckoutFields,
    submitOrder,
    handleInvalidWantedTime,
    replaceWithOrderStatus,
    addNoticeBanner,
  ]);

  // ───── Sync Delivery Order Details ──────────────────────────────
  useSyncDeliveryOrderDetails({ addressId, setPayload });

  // ───── Memoized Results ─────────────────────────────────────────
  return useMemo(
    () => ({
      checkoutViewEvent,
      isLoggedOut,
      isCartValid,
      isCustomerValid,
      isLoadingCheckout,
      checkoutCredit,
      availableCredit,
      deliveryTip,
      creditCardOptions,
      hasAddress,
      orderAddress,
      payload,
      submitting,
      submittingAddress,
      canSubmitOrder,
      validateRewardException,
      shouldShowErrorNotice,
      checkoutFieldsValidation,
      refetchCredits,
      validateCheckoutFields,
      handleUpdateAddress,
      setPayload,
      submitOrder: submitOrderCallback,
      resetCheckoutFieldValidation,
    }),
    [
      checkoutViewEvent,
      isLoggedOut,
      isCartValid,
      isCustomerValid,
      isLoadingCheckout,
      checkoutCredit,
      availableCredit,
      deliveryTip,
      creditCardOptions,
      hasAddress,
      orderAddress,
      payload,
      submitting,
      submittingAddress,
      canSubmitOrder,
      validateRewardException,
      shouldShowErrorNotice,
      checkoutFieldsValidation,
      refetchCredits,
      validateCheckoutFields,
      handleUpdateAddress,
      submitOrderCallback,
      resetCheckoutFieldValidation,
    ],
  );
};

// ───── Constants ──────────────────────────────────────────────────

const defaultCheckoutPayload: CheckoutPayload = {
  wantedTime: '',
  billingAccountId: 'ADD',
  useCredit: true,
  dropoffLocationId: null,
  contactNumber: null,
  includeUtensils: false,
};

// ───── Types ──────────────────────────────────────────────────────

type CheckoutDataProps = {
  readonly replaceWithOrderStatus: (orderId: string) => void;
  readonly handleInvalidWantedTime: (wantedTime?: string) => void;
};
